import { Button } from "@/components";
import { useAnimateModal, useDesignMaxTheme } from "@/packages";
import { useNewNavigate, usePensionNameValue } from "@/shared/client";
import { FONT } from "@/styles";
import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { GuideDetail } from "../../../guideDetail";
import { type RoomDescriptionProps } from "./roomDescription.type";

export const RoomDescription = ({ roomDetailInfo, inView }: RoomDescriptionProps) => {
    const pensionName = usePensionNameValue();
    const { onNavigate } = useNewNavigate();
    const { MEDIAS, COLORS } = useDesignMaxTheme();
    const { openModal, setModalContent } = useAnimateModal();
    const { isMobile, isLaptop, isDesktop } = MEDIAS();

    const WrapperPCPaddingRight = isLaptop ? "88px" : "256px";

    const contentPCHeight = isLaptop ? "528PX" : "792px";

    // const containerPCHeight = isLaptop ? "508px" : "792px";
    const containerPCPadding = isLaptop ? "48px" : "80px 80px 80px 24px";

    const roomImgPCMaxHeight = isLaptop ? "354px" : "520px";
    const roomImgBoxPCMaxWidth = isLaptop ? "520px" : "1008px";

    const pensionKrNameFontSize = isLaptop ? 16 : 20;
    const pensionEngNameFontSize = isLaptop ? 32 : 48;
    const roomNamePCFontSize = isLaptop ? 24 : 32;

    const infoBoxPCJustify = isLaptop ? "space-between" : "normal";

    return (
        <Box
            position="absolute"
            w="100%"
            maxH={isMobile ? "552PX" : "none"}
            h={isMobile ? "100%" : contentPCHeight}
            top="50%"
            left="50%"
            zIndex="1"
            transform="translate(-50%, -50%)"
            paddingRight={isMobile ? "0" : WrapperPCPaddingRight}
        >
            <MotionRoomDetailContainer
                position="relative"
                display="flex"
                justifyContent={isLaptop ? "center" : "flex-end"}
                w="100%"
                height="100%"
                p={isMobile ? "24px" : containerPCPadding}
                borderRightRadius="16px"
                background="white"
                initial={{ width: "10%" }}
                animate={inView ? { width: "100%", transition: { duration: 0.5 } } : {}}
            >
                <MotionContent
                    maxW={isDesktop ? "1424px" : "1744px"}
                    w="100%"
                    h="100%"
                    display="flex"
                    flexDir="column"
                    paddingLeft={isLaptop ? "0" : "24px"}
                    rowGap={isLaptop ? "16px" : "24px"}
                    initial={{ visibility: "hidden", opacity: 0 }}
                    animate={
                        inView
                            ? {
                                  visibility: "visible",
                                  opacity: 1,
                                  transition: { delay: 0.25, ease: "easeInOut", duration: 0.3 },
                              }
                            : {}
                    }
                >
                    <Box display={isMobile ? "none" : "block"}>
                        <Text {...FONT("notoSerif", isMobile ? 14 : pensionKrNameFontSize, 500)}>
                            {pensionName}의 객실
                        </Text>
                        <Text {...FONT("cormorant", isMobile ? 20 : pensionEngNameFontSize, 700, 1.2)}>ROOMS</Text>
                    </Box>
                    <Flex
                        flex="1"
                        w="100%"
                        flexDir={isMobile ? "column" : "row"}
                        columnGap={isLaptop ? "24px" : "48px"}
                        rowGap={isMobile ? "16px" : "0"}
                    >
                        <Flex
                            columnGap={isLaptop ? "0" : "24px"}
                            maxWidth={isMobile ? "none" : roomImgBoxPCMaxWidth}
                            w="100%"
                            sx={{
                                "& div": {
                                    flex: "1",
                                    maxH: isMobile ? "243px" : roomImgPCMaxHeight,
                                    height: isMobile ? "224px" : "100%",
                                    borderRadius: "16px",
                                },
                            }}
                        >
                            <Box background={`url(${roomDetailInfo.imgGroup.main})no-repeat center/cover`} />
                            <Box
                                display={isLaptop ? "none" : "block"}
                                background={`url(${roomDetailInfo.imgGroup.sub})no-repeat center/cover`}
                            />
                        </Flex>
                        <Flex flex="1" w="100%" height="100%" flexDir="column" alignItems="flex-start">
                            <Flex
                                flexDir="column"
                                h="100%"
                                rowGap={isMobile ? "16px" : "24PX"}
                                justifyContent={isMobile ? "normal" : infoBoxPCJustify}
                            >
                                <Flex flexDir="column" rowGap={isMobile ? "8px" : "16px"}>
                                    <Text {...FONT("notoSerif", isMobile ? 16 : roomNamePCFontSize, 500)}>
                                        {roomDetailInfo.name}
                                    </Text>
                                    <Box
                                        sx={{
                                            "& p": {
                                                ...FONT("pretendard", isMobile ? 14 : 16, 300),
                                                color: COLORS.gray[500],
                                            },
                                        }}
                                    >
                                        <Text>
                                            #기준{roomDetailInfo.personOfCapacity.min}인 #최대
                                            {roomDetailInfo.personOfCapacity.max}인
                                        </Text>
                                        <Text>{roomDetailInfo.structure}</Text>
                                        {/* <Text>{roomDetailInfo.tags}</Text> */}
                                    </Box>
                                </Flex>
                                <Flex flexDir="column" rowGap="8x">
                                    <Text {...FONT("notoSerif", 14, 500)}>AMENITIES & SERVICE 어메니티 & 서비스</Text>
                                    {!isLaptop && (
                                        <Flex columnGap="8px">
                                            {roomDetailInfo.amenities[1].map((amenity) => (
                                                <Box
                                                    w="48px"
                                                    h="48px"
                                                    key={Math.random()}
                                                    background={`url(${amenity.iconUrl})no-repeat center/cover`}
                                                ></Box>
                                            ))}
                                        </Flex>
                                    )}
                                    <Flex
                                        flexWrap="wrap"
                                        sx={{
                                            "& p": {
                                                ...FONT("pretendard", isMobile ? 12 : 14, 300),
                                            },
                                        }}
                                    >
                                        {roomDetailInfo.amenities[0].map((amenity, index) => {
                                            if (index === roomDetailInfo.amenities[0].length - 1)
                                                return <Text key={Math.random()}>{amenity.name}</Text>;

                                            return (
                                                <Text key={Math.random()} marginRight="4px">
                                                    {amenity.name},
                                                </Text>
                                            );
                                        })}
                                    </Flex>
                                </Flex>
                                <Flex
                                    h="100%"
                                    alignItems="flex-end"
                                    columnGap="16px"
                                    sx={{
                                        "& button": {
                                            w: isMobile ? "100%" : "164px",
                                            h: isMobile ? "48px" : "54px",
                                        },
                                        "& > button:first-of-type": {
                                            backgroundColor: COLORS.gray[500],
                                            border: `1px solid ${COLORS.gray[500]}`,
                                            color: COLORS.white,
                                        },
                                    }}
                                >
                                    <Button onClick={() => onNavigate({ path: "/reserve" })}>예약하기</Button>
                                    <Button
                                        onClick={() => {
                                            openModal();
                                            setModalContent(<GuideDetail />);
                                        }}
                                    >
                                        유의사항
                                    </Button>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Flex>
                </MotionContent>
            </MotionRoomDetailContainer>
        </Box>
    );
};

const MotionRoomDetailContainer = chakra(motion.div);
const MotionContent = chakra(motion.div);
